import { ArrowLeftCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSystemScenarios, useUserScenarios } from "./store";
import { type Scenario } from "../../types";
import { useAtomValue } from "jotai";
import { modalityAtom, nativeLanguageAtom, practiceLanguageAtom } from "../../stores/app";

interface ScenarioViewerProps {
  scenario: Scenario;
}

const ScenarioEditor = () => {
  const { scenarioId = "" } = useParams<{ scenarioId: string }>();
  const { userScenarios, updateUserScenario } = useUserScenarios();
  const editableScenario = userScenarios.find((s) => s.id === scenarioId);

  const handleChange = (updates: Partial<Scenario>) => {
    if (scenarioId) {
      updateUserScenario({ ...editableScenario!, ...updates });
    }
  };

  if (!editableScenario) return null;

  return (
    <div className="space-y-6">
      <div>
        <label className="label">
          <span className="label-text">Scenario Title</span>
        </label>
        <input
          type="text"
          value={editableScenario.title}
          onChange={(e) => handleChange({ title: e.target.value })}
          className="input input-bordered w-full"
          placeholder="Give your scenario a descriptive title"
        />
      </div>


      <div>
        <label className="block text-sm font-medium text-base-content mb-2">
          Instructions
        </label>
        <textarea
          value={editableScenario.instructions}
          onChange={(e) => handleChange({ instructions: e.target.value })}
          className="textarea textarea-bordered w-full h-32"
        />
      </div>
    </div>
  );
};

const ScenarioViewer = ({ scenario }: ScenarioViewerProps) => {
  return (
    <div className="space-y-6">
      <div className="form-control">
        <label className="label">
          <span className="label-text">Instructions</span>
        </label>
        <div className="card bg-base-200">
          <div className="card-body min-h-[8rem] max-h-[12rem] overflow-y-auto">
            {scenario.instructions}
          </div>
        </div>
      </div>
    </div>
  );
};

const PracticeControls = ({ 
  modality, 
  setModality, 
  onStart 
}: { 
  modality: "audio" | "text";
  setModality: (modality: "audio" | "text") => void;
  onStart: () => void;
}) => {
  return (
    <div className="space-y-4 mt-6">
      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
        <label className="label">
          <span className="label-text">Response Type:</span>
        </label>
        <div className="flex rounded-md shadow-sm">
          <button
            onClick={() => setModality("audio")}
            className={`btn join-item ${
              modality === "audio" ? "btn-primary" : ""
            }`}
          >
            Voice
          </button>
          <button
            onClick={() => setModality("text")}
            className={`btn join-item ${
              modality === "text" ? "btn-primary" : ""
            }`}
          >
            Text
          </button>
        </div>
      </div>

      <button onClick={onStart} className="btn btn-primary">
        Start Practice
      </button>
    </div>
  );
};

export const ScenarioPreview = () => {
  const [isLoading, setIsLoading] = useState(true);
  const defaultModality = useAtomValue(modalityAtom);
  const [modality, setModality] = useState<"audio" | "text">(defaultModality);
  const navigate = useNavigate();
  const { scenarioId = "" } = useParams<{ scenarioId: string }>();
  const practiceLanguage = useAtomValue(practiceLanguageAtom);
  const nativeLanguage = useAtomValue(nativeLanguageAtom);

  const { userScenarios, updateUserScenario } = useUserScenarios();
  const userScenario = userScenarios.find((s) => s.id === scenarioId);

  const systemScenarios = useSystemScenarios();
  const systemScenario = systemScenarios.find((s) => s.id === scenarioId);

  useEffect(() => {
    const initializeScenario = async () => {
      if (
        scenarioId.startsWith("custom-") &&
        !userScenarios.find((s) => s.id === scenarioId)
      ) {
        const newScenario = {
          id: scenarioId,
          title: "Custom Practice Scenario",
          instructions: `<Instructions for the assistant>, e.g.          
You are a local real-estate agent specializing in rentals.
You help clients find local apartments suitable for them.
You walk through the process of identifying appropriate apartments, scheduling viewings, and negotiating leases.

A client has entered and needs assistance.
`,
        };
        updateUserScenario(newScenario);
      }
      setIsLoading(false);
    };

    initializeScenario();
  }, [scenarioId, userScenarios, updateUserScenario]);

  const scenario = userScenario || systemScenario;

  const handleStart = () => {
    if (scenario) {
      // URL encode the instructions for the query parameter
      const encodedInstructions = encodeURIComponent(scenario.instructions);
      navigate(`/practice/chat?instructions=${encodedInstructions}&practiceLanguage=${practiceLanguage}&nativeLanguage=${nativeLanguage}&modality=${modality}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (scenario === undefined) {
    return <div>Failed to find matching scenario for {scenarioId}</div>;
  }

  return (
    <div className="-m-8 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 mb-4">
          <button
            onClick={() => navigate("/scenarios")}
            className="btn btn-ghost btn-sm gap-2"
          >
            <ArrowLeftCircleIcon className="h-5 w-5" />
            <span>Back to scenarios</span>
          </button>
        </div>

        <div className="bg-base-100 rounded-lg p-6 shadow-lg">
          {scenarioId.startsWith("custom-") ? (
            <ScenarioEditor />
          ) : (
            <ScenarioViewer scenario={scenario} />
          )}
          <PracticeControls 
            modality={modality}
            setModality={setModality}
            onStart={handleStart}
          />
        </div>
      </div>
    </div>
  );
};
